import { message } from 'antd';
import { mediaTypeOfUrl, isRenderableMediaUrl } from '../../screens/app/createScreen/utils/parameterUtils';

// Constants
export const NANO_BANANA_PRO_MODEL_ID = '2a572435-d02b-4a9f-96d9-c34c5f0a95ef';

export const RENDER_MODE = {
  MODAL: 'modal',
  DRAWER: 'drawer',
  SCREEN: 'screen',
};

export const STATUS_COLORS = {
  completed: '#22c55e',
  processing: '#3b82f6',
  pending: '#f59e0b',
  failed: '#ef4444',
};

export const STATUS_ICONS = {
  completed: '✓',
  processing: '⟳',
  pending: '⏳',
  failed: '✕',
};

// Determine job type
export const getJobType = (data) => {
  if (!data) return null;
  if (data.image_job_id) return 'image';
  if (data.workflow_job_id) return 'workflow';
  // Zen mode: check for model vs workflow object
  if (data.model?.model_id) return 'image';
  if (data.workflow?.workflow_definition_id) return 'workflow';
  // Fallback: if has images array, treat as image job
  if (Array.isArray(data.images) || Array.isArray(data.outputs)) return 'image';
  return null;
};

export const getJobId = (data) => {
  if (!data) return null;
  return data.image_job_id || data.workflow_job_id || data.id || data.job_id;
};

// Extract ALL input images from job data
export const extractInputImages = (data) => {
  if (!data) return [];
  
  const images = [];
  const seen = new Set();
  
  const addImage = (url, label, source) => {
    if (!url || seen.has(url)) return;
    if (typeof url !== 'string') return;
    if (!url.startsWith('http') && !url.startsWith('data:')) return;
    
    seen.add(url);
    images.push({
      id: `input_${images.length}`,
      url,
      label: label || `Input ${images.length + 1}`,
      source,
      // Was hardcoded 'image'. Now that video/audio inputs pass the URL gate
      // below, mislabelling them here would render a .mp4 into an <img>.
      type: mediaTypeOfUrl(url),
    });
  };
  
  // Check input_params for any key containing 'image' or 'url'
  const inputParams = data.input_params || {};
  Object.entries(inputParams).forEach(([key, value]) => {
    const keyLower = key.toLowerCase();
    const isImageKey = keyLower.includes('image') || 
                       keyLower.includes('url') || 
                       keyLower.includes('img') ||
                       keyLower.includes('photo');
    
    if (typeof value === 'string' && isImageKey) {
      // Shared gate — video/audio included so reference-video and
      // reference-audio inputs stop being silently dropped from job history.
      if (isRenderableMediaUrl(value)) {
        addImage(value, formatLabel(key), 'input_params');
      }
    } else if (Array.isArray(value)) {
      // Handle array of URLs (image_urls)
      value.forEach((item, idx) => {
        if (typeof item === 'string') {
          addImage(item, `${formatLabel(key)} ${idx + 1}`, 'input_params');
        } else if (item?.url || item?.external_url) {
          addImage(item.url || item.external_url, `${formatLabel(key)} ${idx + 1}`, 'input_params');
        }
      });
    }
  });
  
  // Check input_images object
  const inputImages = data.input_images || {};
  if (typeof inputImages === 'object') {
    Object.entries(inputImages).forEach(([key, value]) => {
      if (typeof value === 'string') {
        addImage(value, formatLabel(key), 'input_images');
      } else if (Array.isArray(value)) {
        value.forEach((url, idx) => {
          if (typeof url === 'string') {
            addImage(url, `${formatLabel(key)} ${idx + 1}`, 'input_images');
          }
        });
      }
    });
  }
  
  return images;
};

// Extract output results
export const extractOutputResults = (data) => {
  if (!data) return [];
  
  const outputs = [];
  const jobType = getJobType(data);
  
  // Get results array based on job type and data structure
  let results = [];
  
  // Standard API format
  if (jobType === 'image' && data.results) {
    results = Array.isArray(data.results) ? data.results : [];
  } else if (jobType === 'workflow' && data.results?.results) {
    results = Array.isArray(data.results.results) ? data.results.results : [];
  }
  
  // Zen mode format - check for images/outputs arrays directly
  if (results.length === 0 && Array.isArray(data.images)) {
    data.images.forEach((url, index) => {
      if (typeof url === 'string') {
        outputs.push({
          id: `output_${index}`,
          url,
          content: null,
          type: 'image',
          label: `Output ${index + 1}`,
          metadata: {},
        });
      }
    });
  }
  
  if (results.length === 0 && Array.isArray(data.outputs)) {
    data.outputs.forEach((item, index) => {
      if (typeof item === 'string') {
        outputs.push({
          id: `output_${index}`,
          url: item,
          content: null,
          type: 'image',
          label: `Output ${index + 1}`,
          metadata: {},
        });
      } else if (item?.url || item?.external_url) {
        outputs.push({
          id: `output_${index}`,
          url: item.url || item.external_url,
          content: item.content_type === 'text' ? item.content : null,
          type: item.content_type || 'image',
          label: `Output ${index + 1}`,
          metadata: item.additional_params || {},
        });
      }
    });
  }
  
  // Process standard results format
  results.forEach((result, index) => {
    if (!result) return;
    
    const url = result.external_url || result.content || result.url;
    const contentType = result.content_type || 'image';
    
    outputs.push({
      id: `output_${index}`,
      url: contentType !== 'text' ? url : null,
      content: contentType === 'text' ? (result.content || url) : null,
      type: contentType,
      label: `Output ${index + 1}`,
      metadata: result.additional_params || {},
    });
  });
  
  return outputs;
};

// Extract prompts/text inputs
export const extractPrompts = (data) => {
  if (!data) return {};
  
  const prompts = {};
  const inputParams = data.input_params || {};
  const inputTexts = data.input_texts || {};
  
  // Common prompt keys to look for
  const promptKeys = ['prompt', 'positive_prompt', 'negative_prompt', 'text', 'caption', 'query', 'input_text'];
  
  // Check input_params
  Object.entries(inputParams).forEach(([key, value]) => {
    if (typeof value !== 'string') return;
    if (value.startsWith('http')) return; // Skip URLs
    
    const keyLower = key.toLowerCase();
    if (promptKeys.some(pk => keyLower.includes(pk))) {
      prompts[key] = {
        label: formatLabel(key),
        content: value,
        source: 'input_params',
      };
    }
  });
  
  // Check input_texts (if not already found)
  if (typeof inputTexts === 'object') {
    Object.entries(inputTexts).forEach(([key, value]) => {
      if (value && !prompts[key]) {
        prompts[key] = {
          label: formatLabel(key),
          content: value,
          source: 'input_texts',
        };
      }
    });
  }
  
  return prompts;
};

// Format parameter label
export const formatLabel = (key) => {
  return key
    .replace(/_/g, ' ')
    .replace(/url/gi, '')
    .replace(/image/gi, 'Image')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim() || key;
};

// Format duration
export const formatDuration = (seconds) => {
  if (!seconds || isNaN(seconds)) return '-';
  const numSeconds = parseFloat(seconds);
  if (isNaN(numSeconds)) return '-';
  if (numSeconds < 60) return `${numSeconds.toFixed(1)}s`;
  const minutes = Math.floor(numSeconds / 60);
  const remainingSeconds = numSeconds % 60;
  return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
};

// Format date relative
export const formatRelativeDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};

// Format cost
export const formatCost = (cost) => {
  if (cost === undefined || cost === null) return '-';
  return `${Number(cost).toFixed(4)} URO`;
};

// Copy to clipboard
export const copyToClipboard = async (text) => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(String(text));
    } else {
      const ta = document.createElement('textarea');
      ta.value = String(text);
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    message.success('Copied to clipboard');
    return true;
  } catch (err) {
    message.error('Failed to copy');
    return false;
  }
};

// Get file extension from mime type or URL
const getFileExtension = (mimeType, url) => {
  // First try to get extension from mime type
  if (mimeType && mimeType.includes('/')) {
    const ext = mimeType.split('/')[1].split(';')[0];
    if (ext === 'jpeg') return 'jpg';
    if (ext === 'quicktime') return 'mov';
    return ext;
  }
  
  // Fallback: extract from URL
  const match = url.match(/\.([a-z0-9]+)(?:[?#]|$)/i);
  if (match) return match[1];
  
  // Last resort based on URL patterns
  if (url.includes('video') || url.includes('.mp4') || url.includes('.mov') || url.includes('.webm')) return 'mp4';
  return 'png';
};

// Download image/video - auto-detects extension from mime type or URL
export const downloadImage = async (url, filename) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    
    // Auto-detect extension if filename doesn't have one or uses placeholder
    let finalFilename = filename || 'download';
    const ext = getFileExtension(blob.type, url);
    
    // If filename ends with a placeholder extension like .png but actual file is different, fix it
    if (finalFilename.match(/\.(png|jpg|jpeg)$/i) && blob.type && blob.type.startsWith('video/')) {
      finalFilename = finalFilename.replace(/\.(png|jpg|jpeg)$/i, `.${ext}`);
    } else if (!finalFilename.includes('.')) {
      finalFilename = `${finalFilename}.${ext}`;
    }
    
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = finalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
    
    return true;
  } catch (err) {
    console.error('Download failed:', err);
    // Fallback: try direct link
    try {
      const ext = getFileExtension('', url);
      let finalFilename = filename || 'download';
      if (!finalFilename.includes('.')) {
        finalFilename = `${finalFilename}.${ext}`;
      }
      
      const link = document.createElement('a');
      link.href = url;
      link.download = finalFilename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return true;
    } catch (fallbackErr) {
      message.error('Download failed');
      return false;
    }
  }
};

// Download all media - extension auto-detected from actual file
export const downloadAllMedia = async (outputs, jobId) => {
  let success = 0;
  let failed = 0;
  
  for (let i = 0; i < outputs.length; i++) {
    const output = outputs[i];
    if (output.url && output.type !== 'text') {
      // Don't specify extension - let downloadImage auto-detect from actual file
      const result = await downloadImage(output.url, `${jobId}_${i + 1}`);
      if (result) success++;
      else failed++;
    }
  }
  
  if (success > 0) message.success(`Downloaded ${success} files`);
  if (failed > 0) message.warning(`Failed to download ${failed} files`);
  
  return { success, failed };
};

// Get error info
export const getErrorInfo = (data) => {
  if (!data) return null;
  
  const error = data.error || data.failed_message || data.error_message;
  if (!error) return null;
  
  if (typeof error === 'string') {
    return {
      message: error,
      details: null,
    };
  }
  
  return {
    message: error.message || error.error || 'An error occurred',
    details: error.details || error.stack || null,
    code: error.code || null,
  };
};

// Filter non-image params for JSON display
export const filterParamsForDisplay = (inputParams) => {
  if (!inputParams) return {};
  
  const filtered = {};
  const skipKeys = ['webhookURL', 'customTaskUUID'];
  
  Object.entries(inputParams).forEach(([key, value]) => {
    if (skipKeys.includes(key)) return;
    filtered[key] = value;
  });
  
  return filtered;
};

// Build zen edit URL
export const buildZenEditUrl = (imageUrl, modelId = NANO_BANANA_PRO_MODEL_ID) => {
  const params = new URLSearchParams({
    image_url: imageUrl,
  });
  return `/zen/model/${modelId}?${params.toString()}`;
};
